// eslint-disable-next-line import/no-duplicates
import type Quagga from "@ericblade/quagga2/type-definitions/quagga";
// eslint-disable-next-line import/no-duplicates
import type { QuaggaJSResultObject } from "@ericblade/quagga2/type-definitions/quagga";
import { definedMap } from "@glideapps/ts-necessities";
import type { CodeScannerStandards } from "common/code-scanner-standards";
import { CodeScannerStandard, isEnabledCodeScannerStandard } from "common/code-scanner-standards";
import type { AppKind } from "common/components/app-kind";
import { asNumber } from "common/components/component-helpers";
import { logError } from "common/debug-print";
import { Check, RefreshCw, Settings, Upload } from "common/react-plucked";
import { defined, dontAwait, panic } from "common/Support";
import { SyncJobQueue } from "common/sync-job-queue";
import { lazyLoading } from "lib/lazy-loading";
import once from "lodash/once";
import * as React from "react";
import { ScaleRenderContext } from "sharedUI/ui/views/smartphone/smartphone";
import { withTheme } from "styled-components";
import ActivitySpinner from "webapp/chrome/views/activity-spinner/activity-spinner";
import { useRefState } from "webapp/lib/hooks/refs";
import type { PointerPosition } from "webapp/lib/hooks/single-touch-pointer";
import { hookPointerTouchEventsOnElement, usePointerTracking } from "webapp/lib/hooks/single-touch-pointer";
import type { RuntimeTheme } from "webapp/lib/theme/core-themes/runtime-theme";
import { StreamlineCamera } from "../image-carousel/image-carousel-style";
import { CodeScannerWrapper } from "./code-scanner-style";
import { fitBoxIntoBox, geomForPoints, pointInBox } from "./geometry";
import type { ScanPrecision, Tolerance } from "./quagga-bindings";
import {
    decodeErrorAcceptable,
    mungeFormatIntoStandard,
    patchSizeForPrecision,
    quaggaReadersFromCodeStandards,
    quantileForScanTolerance,
    scaleFactorForWidth,
} from "./quagga-bindings";
import { SettingsOverlay } from "./settings-overlay";

type Props = Readonly<{
    theme: RuntimeTheme;
    standards: CodeScannerStandards;
    acceptOnFirst: boolean;
    appKind: AppKind;
    onValue: ((standard: CodeScannerStandard, value: string) => void) | undefined;
}>;

type ScanState =
    | Readonly<{
          kind: "none";
      }>
    | Readonly<{
          kind: "file";
          fileData: File | string;
      }>
    | Readonly<{
          kind: "camera";
      }>;

type ImportState = Readonly<{
    ready: boolean;
    quagga?: typeof Quagga;
    // eslint-disable-next-line @typescript-eslint/consistent-type-imports
    qrcodes?: typeof import("./qrcode-reader");
}>;

function signOf(num: number): number {
    return num < 0 ? -1 : 1;
}

function drawRoundRect(drawCtx: CanvasRenderingContext2D, width: number, height: number, radius: number) {
    drawCtx.moveTo(radius, 0);
    const x2 = width - radius;
    drawCtx.lineTo(x2, 0);
    const y2 = height - signOf(height) * radius;
    drawCtx.arc(x2, signOf(height) * radius, radius, (signOf(height) * -Math.PI) / 2, 0);
    drawCtx.lineTo(width, y2);
    drawCtx.arc(width - radius, y2, radius, 0, (signOf(height) * Math.PI) / 2);
    drawCtx.lineTo(radius, height);
    drawCtx.arc(radius, height - radius, radius, (signOf(height) * Math.PI) / 2, -Math.PI);
    const y4 = signOf(height) * radius;
    drawCtx.lineTo(0, y4);
    drawCtx.arc(radius, y4, radius, -Math.PI, (signOf(height) * -Math.PI) / 2);
}

type DrawRegionRectParams = Readonly<{
    drawCtx: CanvasRenderingContext2D;
    strokeStyle?: string;
    fillStyle: string;
    lineWidth: number;
    radius: number;
    points: readonly (readonly number[])[];
}>;

function drawRegionRect(params: DrawRegionRectParams) {
    const { drawCtx, strokeStyle, fillStyle, lineWidth, radius, points } = params;
    drawCtx.resetTransform();
    drawCtx.beginPath();

    if (strokeStyle !== undefined) {
        drawCtx.strokeStyle = strokeStyle;
    }
    drawCtx.fillStyle = fillStyle;
    drawCtx.lineWidth = lineWidth;

    const [xy1, xy2, xy3] = points;
    const { width, height, angle } = geomForPoints(xy1, xy2, xy3);
    drawCtx.translate(xy1[0], xy1[1]);
    drawCtx.rotate(angle);
    drawRoundRect(drawCtx, width, height, radius);
    drawCtx.fill();
    if (strokeStyle !== undefined) {
        drawCtx.stroke();
    }
    drawCtx.rotate(-angle);
    drawCtx.translate(-xy1[0], -xy1[1]);
}

function findOverlappingResult(
    results: readonly QuaggaJSResultObject[],
    xy: number[]
): QuaggaJSResultObject | undefined {
    for (const result of results) {
        if (result.codeResult === null) continue;
        const [xy1, xy2, xy3] = result.box;
        if (xy1 === undefined || xy2 === undefined || xy3 === undefined) continue;

        if (pointInBox(xy, xy1, xy2, xy3)) return result;
    }
    return undefined;
}

const regionFillStyle = "#0000007f";
const emptyStrokeWidth = 0;
const fullStrokeWidth = 2;
const roundedRectRadius = 8;

function consumeAsSideEffect<T>(_x: T): void {
    // We only do use this to make React's hook linter happy.
}

// Mobile Safari isn't very good at handling the canvas replacement
// here. It turns out, it takes two frames to actually start
// displaying the correct contents of the canvas buffer.
//
// This also unfortunately applies to CSS mutations too!
function afterMobileSafariFrameDelay(f: () => void) {
    let lastTime = window.performance.now();
    let elapsedTime = 0;
    let frameCount = 0;
    const reenter = () =>
        requestAnimationFrame(frameTime => {
            frameCount++;
            elapsedTime += frameTime - lastTime;
            lastTime = frameTime;
            // This is a little less than the time for two
            // frames on a 60 Hz display. We always want at
            // least two frames just to be sure.
            if (elapsedTime > 33.3 && frameCount > 1) {
                f();
                return;
            } else {
                reenter();
            }
        });
    reenter();
}

function CodeScanner({ theme, standards, onValue, acceptOnFirst, appKind }: Props): JSX.Element {
    const [sequencer] = React.useState(new SyncJobQueue());

    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const [videoRef, videoElem] = useRefState<HTMLVideoElement>();
    const [wrapperRef, wrapperElem] = useRefState<HTMLDivElement>();

    const [containerRef, containerElem] = useRefState<HTMLDivElement>();
    const [imageCanvasRef, imageCanvasElem] = useRefState<HTMLCanvasElement>();
    const [drawCanvasRef, drawCanvasElem] = useRefState<HTMLCanvasElement>();

    const [orientation, setOrientation] = React.useState<"portrait" | "landscape" | undefined>();
    const [scanState, setScanState] = React.useState<ScanState>({ kind: "none" });
    const [importState, setImportState] = React.useState<ImportState>({ ready: false });
    const [scanPrecision, setScanPrecision] = React.useState<ScanPrecision>("normal");
    const [scanTolerance, setScanTolerance] = React.useState<Tolerance>("normal");
    const [scanResults, setScanResults] = React.useState<readonly QuaggaJSResultObject[]>([]);

    const [currentSelectedResult, setCurrentSelectedResult] = React.useState<QuaggaJSResultObject | undefined>();
    const [priorSelectedResult, setPriorSelectedResult] = React.useState<QuaggaJSResultObject | undefined>();
    const [scaleFactor, setScaleFactor] = React.useState<number | undefined>();

    const outerScale = React.useContext(ScaleRenderContext);
    const [innerScale, setInnerScale] = React.useState<number>(1);
    const [innerOffsetX, setInnerOffsetX] = React.useState<number>(0);
    const [innerOffsetY, setInnerOffsetY] = React.useState<number>(0);

    const [workingCamera, setWorkingCamera] = React.useState<MediaStream | false | undefined>(
        navigator?.mediaDevices?.getUserMedia === undefined ? false : undefined
    );

    // Unfortunately QR codes did not integrate as cleanly with Quagga as their docs
    // would have suggested. So we have to keep two separate scan result sets
    // and merge them with a useEffect
    const [barcodeScanResults, setBarcodeScanResults] = React.useState<readonly QuaggaJSResultObject[]>([]);
    const [qrScanResults, setQrScanResults] = React.useState<readonly QuaggaJSResultObject[]>([]);

    React.useEffect(() => {
        setScanResults([...qrScanResults, ...barcodeScanResults]);
    }, [barcodeScanResults, qrScanResults, setScanResults]);

    const clearResults = React.useCallback(() => {
        setBarcodeScanResults([]);
        setQrScanResults([]);
    }, [setBarcodeScanResults, setQrScanResults]);

    const [settingsActive, setSettingsActive] = React.useState<boolean>(false);
    const toggleSettings = React.useCallback(() => {
        setSettingsActive(!settingsActive);
    }, [settingsActive, setSettingsActive]);
    React.useEffect(() => {
        setSettingsActive(false);
    }, [scanState, setSettingsActive]);

    // Many internal image handling calculations are based on the
    // bounding rectangle of the container.
    const getUnscaledContainerDims = React.useCallback(() => {
        if (containerElem === undefined) return undefined;

        const { width, height } = containerElem.getBoundingClientRect();
        return { width: width / outerScale, height: height / outerScale };
    }, [outerScale, containerElem]);

    const containerWidth = definedMap(getUnscaledContainerDims(), d => d.width) ?? 0;
    const containerHeight = definedMap(getUnscaledContainerDims(), d => d.height) ?? 0;

    // We don't want to emit the same value any more than once per state.
    const emitValue = React.useMemo(() => {
        if (onValue === undefined) {
            return;
        }
        consumeAsSideEffect(scanState);
        return once(onValue);
    }, [onValue, scanState]);

    // We receive mouse events on the whole container, but we want to figure
    // out mouse positioning relative to the drawing canvas instead. Hence,
    // this callback.
    const correctMousePosition = React.useCallback(
        (pos: PointerPosition) => {
            if (drawCanvasElem === undefined) return undefined;

            const { x, y } = drawCanvasElem.getBoundingClientRect();
            const screenScale = outerScale * innerScale;
            // Note that the innerScale already accounts for the scaleFactor,
            // so we don't need to take it into account.
            return {
                x: (pos.x - x - innerOffsetX * outerScale) / screenScale,
                y: (pos.y - y - innerOffsetY * outerScale) / screenScale,
            };
        },
        [drawCanvasElem, outerScale, innerScale, innerOffsetX, innerOffsetY]
    );

    const resultIsAcceptable = React.useCallback(
        (res: QuaggaJSResultObject) => {
            return (
                res.codeResult !== null &&
                res.codeResult !== undefined &&
                res.codeResult.code !== null &&
                decodeErrorAcceptable(res.codeResult, quantileForScanTolerance(scanTolerance))
            );
        },
        [scanTolerance]
    );

    const getDrawCtx = React.useMemo(
        () =>
            (() => {
                let drawCtx: CanvasRenderingContext2D | undefined;
                return () => {
                    if (drawCtx !== undefined) return drawCtx;
                    if (drawCanvasElem === undefined) return undefined;
                    drawCtx = drawCanvasElem.getContext("2d") ?? undefined;
                    return drawCtx;
                };
            })(),
        [drawCanvasElem]
    );

    const getImageCanvasCtx = React.useMemo(
        () =>
            (() => {
                let ctx: CanvasRenderingContext2D | undefined;
                return () => {
                    if (ctx !== undefined) return ctx;
                    if (imageCanvasElem === undefined) return undefined;
                    ctx = imageCanvasElem.getContext("2d") ?? undefined;
                    return ctx;
                };
            })(),
        [imageCanvasElem]
    );

    const clearCanvas = React.useCallback(() => {
        if (drawCanvasElem === undefined) return;
        const drawCtx = getDrawCtx();
        if (drawCtx === undefined) return;

        const width = asNumber(drawCanvasElem.getAttribute("width"));
        const height = asNumber(drawCanvasElem.getAttribute("height"));
        drawCtx.resetTransform();
        drawCtx.beginPath();
        drawCtx.clearRect(0, 0, width, height);
    }, [drawCanvasElem, getDrawCtx]);

    // Quagga is kind of dumb with respect to its treatment of "size":
    // it's the maximum height _and_ width, and whichever is smaller is coerced
    // into scaling appropriately. Except,
    // 1. If the width is less than the height we want a full width
    // 2. Otherwise we want a full height
    // So we have to scale _out_ after scaling down.
    //
    // Yeah it's pretty nasty.
    // We get to visually do this using object-fit but internally we need to
    // know that scale for mouse position correction, so we still have to
    // compute it ourselves anyway.
    const rescaleCanvas = React.useCallback(
        (imgWidth: number, imgHeight: number) => {
            const unscaledDims = getUnscaledContainerDims();
            if (unscaledDims === undefined) return;
            if (imgWidth === 0 || imgHeight === 0) return;

            const { scale, x, y } = fitBoxIntoBox({ width: imgWidth, height: imgHeight }, unscaledDims);
            setInnerScale(scale);
            setInnerOffsetX(x);
            setInnerOffsetY(y);
        },
        [getUnscaledContainerDims, setInnerScale, setInnerOffsetX, setInnerOffsetY]
    );

    // This one's a bit tricky in terms of how it gets called:
    //
    // The consumer below in a useEffect will avoid re-drawing the
    // canvas during a mouse move if the canvas result is unlikely
    // to change. It does so by detecting whether the confirmed result
    // is going to be any different. By default, there's no confirmed
    // result, which means that the first time you click on a region not
    // inside a confirmed result, it looks like there's no difference.
    //
    // We have to do this additionally on mouse down to fix that artifact.
    // Which is why it's up here, above the mouse handlers.
    const drawAcceptableSelectView = React.useCallback(() => {
        const drawCtx = getDrawCtx();
        if (drawCtx === undefined || scaleFactor === undefined) return;
        clearCanvas();
        for (const result of scanResults.filter(resultIsAcceptable)) {
            drawRegionRect({
                drawCtx,
                fillStyle: regionFillStyle,
                strokeStyle: Object.is(result, currentSelectedResult) ? theme.primaryAccentColor : theme.fgColorOffBase,
                lineWidth: fullStrokeWidth * scaleFactor,
                radius: roundedRectRadius * scaleFactor,
                points: result.box,
            });
        }
    }, [getDrawCtx, clearCanvas, scanResults, resultIsAcceptable, currentSelectedResult, theme, scaleFactor]);

    const [confirmedMousePos, setConfirmedMousePos] = React.useState<PointerPosition | undefined>();
    const [mouseState, mouseStateSetters, touchHandlers] = usePointerTracking({
        handleMouseUp: setConfirmedMousePos,
        handleMouseDown: drawAcceptableSelectView,
    });

    const onFileChange = React.useCallback(
        (ev: React.ChangeEvent<HTMLInputElement>) => {
            ev.preventDefault();
            ev.stopPropagation();
            const file = (ev.target.files ?? [])[0];
            if (file === undefined) {
                if (scanState.kind !== "none") {
                    setScanState({ kind: "none" });
                }
                return;
            }
            setScanState({ kind: "file", fileData: file });
        },
        [scanState.kind, setScanState]
    );

    const onCameraClick = React.useCallback(() => {
        if (typeof workingCamera !== "object") return;
        if (scanState.kind !== "camera") {
            setScanState({ kind: "camera" });
        } else if (scanState.kind === "camera") {
            if (videoElem === undefined || imageCanvasElem === undefined) return;
            const imageCanvasCtx = getImageCanvasCtx();
            if (imageCanvasCtx === undefined) return;

            const { videoWidth, videoHeight } = videoElem;
            const readDims = fitBoxIntoBox(
                { width: containerWidth, height: containerHeight },
                { width: videoWidth, height: videoHeight }
            );
            imageCanvasElem.width = readDims.width;
            imageCanvasElem.height = readDims.height;
            imageCanvasCtx.drawImage(
                videoElem,
                readDims.x,
                readDims.y,
                readDims.width,
                readDims.height,
                0,
                0,
                imageCanvasElem.width,
                imageCanvasElem.height
            );
            rescaleCanvas(imageCanvasElem.width, imageCanvasElem.height);
            afterMobileSafariFrameDelay(() => {
                setScanState({ kind: "file", fileData: imageCanvasElem.toDataURL("image/png") });
            });
        }
    }, [
        workingCamera,
        scanState.kind,
        setScanState,
        videoElem,
        containerWidth,
        containerHeight,
        imageCanvasElem,
        getImageCanvasCtx,
        rescaleCanvas,
    ]);

    // We could hypothetically have done this in a <Suspense> to the same
    // effect, except for the optional QR code scanning. That's another
    // lazy import that also blocks any functionality, so we launder the
    // imports into the state since <Suspense> wouldn't deal with that
    // case anyway.
    React.useEffect(() => {
        lazyLoading("quagga2", true, () => import("@ericblade/quagga2"))
            .then(mod => {
                if (isEnabledCodeScannerStandard(standards, CodeScannerStandard.QR)) {
                    lazyLoading("qrcodes", true, () => import("./qrcode-reader"))
                        .then(rmod => setImportState({ ready: true, quagga: mod.default, qrcodes: rmod }))
                        .catch(panic);
                } else {
                    setImportState({ ready: true, quagga: mod.default });
                }
            })
            .catch(panic);
        return () => {
            setImportState({ ready: false });
        };
    }, [standards, setImportState]);

    // The internal scale factor is a function of the known width
    // of the container.
    React.useEffect(() => {
        const unscaledDims = getUnscaledContainerDims();
        if (unscaledDims === undefined) return;
        setScaleFactor(scaleFactorForWidth(unscaledDims.width));
    }, [getUnscaledContainerDims, setScaleFactor]);

    // We need to know the visual orientation in JavaScript for two reasons:
    // 1. Since the app runs within the builder in the same document as the
    //    builder itself, we cannot use CSS media queries to responsively
    //    layout at the stylesheet level, since the builder's dimensions would
    //    pollute the player context.
    //    (@jassmith when you see this, this is yet another reason to switch
    //     to an every-screen-an-iframe architecture)
    // 2. We need to ensure that the browser appropriately re-orients the
    //    camera according to the device orientation; while iOS figures out
    //    that the orientation of the video doesn't match the orientation of
    //    the containing element and corrects the orientation (after a frame
    //    delay, see below), there's no guarantee that it will automatically
    //    fixup the orientation again if it changes. Android is a complete
    //    mystery as of this writing.
    //
    // The implication is that we need to record the appropriate orientation
    // for computed CSS and also abandon our open camera context.
    React.useEffect(() => {
        if (wrapperElem === undefined) return;

        let lastOrientation: "portrait" | "landscape" | undefined = undefined;
        const setOrientationForElem = () => {
            const { width, height } = wrapperElem.getBoundingClientRect();
            const nextOrientation = width < height ? "portrait" : "landscape";
            if (lastOrientation !== nextOrientation) {
                setOrientation(nextOrientation);
                if (lastOrientation !== undefined) {
                    setWorkingCamera(undefined);
                }
                lastOrientation = nextOrientation;
            }
        };
        window.addEventListener("resize", setOrientationForElem);
        setOrientationForElem();
        return () => {
            window.removeEventListener("resize", setOrientationForElem);
            setOrientation(undefined);
        };
    }, [wrapperElem, setOrientation, setWorkingCamera]);

    React.useEffect(() => {
        if (containerElem === undefined || videoElem === undefined) return;
        if (workingCamera === false) return;
        if (workingCamera !== undefined) {
            // We can't return this cleanup function below because the
            // process of getting a camera media context is asynchronous.
            // Instead, when we detect that we finally have the camera,
            // we set up the cleanup here.
            return () => {
                for (const track of workingCamera.getTracks()) {
                    track.stop();
                }
            };
        }
        if (navigator?.mediaDevices?.getUserMedia === undefined) return;

        const { width, height } = containerElem.getBoundingClientRect();
        // Assuming a landscape orientation, these are acceptable
        const idealAspectRatio = width < height ? 2 / 3 : 1.5;

        dontAwait(
            sequencer.run(async () => {
                try {
                    const camera = await navigator.mediaDevices.getUserMedia({
                        video: { facingMode: ["environment"], width: 1280, aspectRatio: { ideal: idealAspectRatio } },
                    });
                    defined(videoElem).srcObject = camera;
                    afterMobileSafariFrameDelay(() => setWorkingCamera(camera));
                } catch {
                    setWorkingCamera(false);
                }
            })
        );
        return;
    }, [workingCamera, videoElem, containerElem, sequencer, setWorkingCamera]);

    React.useEffect(() => {
        if (containerElem === undefined) return;
        // Mobile Safari is a special snowflake and needs particular
        // touch event handling to not perform dumb overscroll behavior.
        // That's why hookPointerTouchEventsOnElement specifically exists
        // outside of the simple on* handlers.
        return hookPointerTouchEventsOnElement(touchHandlers, containerElem);
    }, [touchHandlers, containerElem]);

    React.useEffect(() => {
        if (typeof workingCamera !== "object") return;
        if (scanState.kind !== "none") return;
        // At this point we have a camera but a scan state of "none";
        // this means we're implicitly in the "camera" state.
        setScanState({ kind: "camera" });
    }, [scanState, workingCamera]);

    React.useEffect(() => {
        if (videoElem === undefined || scanState.kind !== "camera" || typeof workingCamera !== "object") return;
        // We have to do this two frames later to eliminate a canvas drawing
        // flicker on Mobile Safari in the cleanup, and since the cleanup is
        // behind two frames then the setup also has to be behind two frames.
        //
        // This relies on a standard behavior of requestAnimationFrame, where if
        // you have two requestAnimationFrame calls:
        // 1. requestAnimationFrame(A);
        // 2. requestAnimationFrame(B);
        //
        // Then within the same frame, A will run before B. Naturally, if B gets
        // scheduled for the next frame, B will run after A regardless.
        afterMobileSafariFrameDelay(() => videoElem.setAttribute("style", "display: block"));
        return () => {
            afterMobileSafariFrameDelay(() => videoElem.removeAttribute("style"));
        };
    }, [videoElem, scanState, workingCamera]);

    React.useEffect(() => {
        // Whenever we transition out of a "file" state, we need to clear
        // out the <input>'s value to force it to re-trigger onFileChange
        // on file selection.
        if (scanState.kind !== "file" && fileInputRef.current !== null) {
            fileInputRef.current.value = "";
        }
    }, [scanState]);

    // This is the main event: binding Quagga and jsQR into the component.
    React.useEffect(() => {
        if (
            importState.quagga === undefined ||
            scanState.kind === "none" ||
            containerElem === undefined ||
            imageCanvasElem === undefined
        ) {
            return;
        }
        if (scaleFactor === undefined) return;
        const unscaledDims = getUnscaledContainerDims();
        if (unscaledDims === undefined) return;
        const { quagga, qrcodes } = importState;

        const readers = quaggaReadersFromCodeStandards(standards);
        clearResults();

        // We're currently not doing real-time camera capture but instead having
        // the user "take a picture"; when they do this they will end up in the
        // "file" state, and that's when we try to process the image.
        //
        // Take note: Quagga's canvas export only works if debug mode is enabled.
        // That's why we address it directly.
        if (scanState.kind === "file") {
            const { fileData } = scanState;
            const fileURL = typeof fileData === "string" ? fileData : URL.createObjectURL(fileData);
            const onProcessed = (res: any) => {
                const bufWidth = asNumber(imageCanvasElem.getAttribute("width"));
                const bufHeight = asNumber(imageCanvasElem.getAttribute("height"));
                rescaleCanvas(bufWidth, bufHeight);

                if (!Array.isArray(res) || res.length === 0) {
                    setBarcodeScanResults([]);
                } else {
                    setBarcodeScanResults(res);
                }

                const imageCtx = getImageCanvasCtx();
                if (qrcodes !== undefined && imageCtx !== undefined) {
                    const imageData = imageCtx.getImageData(0, 0, bufWidth, bufHeight);
                    const results = qrcodes.decodeQrCode(imageData, bufWidth, bufHeight);
                    setQrScanResults(results);
                }
            };

            const quaggaCleanup = () => {
                if (!fileURL.startsWith("data:")) {
                    URL.revokeObjectURL(fileURL);
                }
                quagga.stop();
                quagga.offProcessed(onProcessed);
            };

            quagga.onProcessed(onProcessed);
            try {
                quagga
                    .decodeSingle({
                        src: fileURL,
                        decoder: {
                            readers,
                            multiple: true,
                        },
                        inputStream: {
                            type: "ImageStream",
                            target: containerElem,
                            // Our sample size ought to be bigger than the actual
                            // view size; this will help with accuracy. Not too
                            // much bigger though, that causes performance problems.
                            size: Math.min(unscaledDims.width, unscaledDims.height) * scaleFactor,
                        },
                        locate: true,
                        locator: {
                            patchSize: patchSizeForPrecision(scanPrecision),
                        },
                    })
                    .catch(e => {
                        logError("Quagga decodeSingle:", e);
                        setScanState({ kind: "none" });
                    });

                return () => {
                    quaggaCleanup();
                    clearResults();
                };
            } catch (e: unknown) {
                logError("Quagga decodeSingle setup:", e);
                quaggaCleanup();
                return;
            }
        } else {
            return;
        }
    }, [
        standards,
        importState,
        scanState,
        scanPrecision,
        scaleFactor,
        getUnscaledContainerDims,
        setBarcodeScanResults,
        setQrScanResults,
        clearResults,
        acceptOnFirst,
        containerElem,
        imageCanvasElem,
        getImageCanvasCtx,
        rescaleCanvas,
    ]);

    // We need to draw the bounding boxes based on
    // 1. Whether the mouse is down
    // 2. If it is, what scan results are valid
    // 3. If they are, does the mouse overlap?
    React.useEffect(() => {
        const drawCtx = getDrawCtx();
        if (drawCtx === undefined || scaleFactor === undefined) return;

        if (mouseState === undefined) {
            // With the mouseState being undefined, we need to clear the canvas before drawing
            clearCanvas();
            for (const result of scanResults.filter(r => !resultIsAcceptable(r))) {
                drawRegionRect({
                    drawCtx,
                    fillStyle: regionFillStyle,
                    lineWidth: emptyStrokeWidth * scaleFactor,
                    radius: roundedRectRadius * scaleFactor,
                    points: result.box,
                });
            }
            for (const result of scanResults.filter(resultIsAcceptable)) {
                drawRegionRect({
                    drawCtx,
                    fillStyle: regionFillStyle,
                    strokeStyle: theme.fgColorOffBase,
                    lineWidth: fullStrokeWidth * scaleFactor,
                    radius: roundedRectRadius * scaleFactor,
                    points: result.box,
                });
            }
        } else if (!Object.is(priorSelectedResult, currentSelectedResult)) {
            // We only need to do this if the selected result changes.
            drawAcceptableSelectView();
        }
    }, [
        theme,
        scaleFactor,
        getDrawCtx,
        scanState,
        scanResults,
        mouseState,
        currentSelectedResult,
        priorSelectedResult,
        setCurrentSelectedResult,
        setPriorSelectedResult,
        resultIsAcceptable,
        clearCanvas,
        drawAcceptableSelectView,
    ]);

    // If there's only 1 effective value, we can return it after a timeout.
    React.useEffect(() => {
        const acceptableResults = scanResults.filter(resultIsAcceptable);

        if (emitValue !== undefined && acceptableResults.length === 1 && acceptOnFirst) {
            const [{ codeResult }] = scanResults;
            const oneValueTimeout = setTimeout(() => {
                if (codeResult.code !== null) {
                    emitValue(mungeFormatIntoStandard(codeResult.format), codeResult.code);
                }
            }, 500);
            return () => clearTimeout(oneValueTimeout);
        }

        return;
    }, [resultIsAcceptable, scanResults, emitValue, acceptOnFirst]);

    // Otherwise we require a mouse click within the bounds of an effective value.
    React.useEffect(() => {
        if (confirmedMousePos === undefined) return;
        setConfirmedMousePos(undefined);

        if (scanResults === undefined) return;
        const correctPos = correctMousePosition(confirmedMousePos);
        if (correctPos === undefined) return;

        const overlapping = findOverlappingResult(scanResults.filter(resultIsAcceptable), [correctPos.x, correctPos.y]);
        if (overlapping !== undefined) {
            const { codeResult } = overlapping;
            if (emitValue === undefined || codeResult.code === null) return;
            emitValue(mungeFormatIntoStandard(codeResult.format), codeResult.code);
        }
    }, [confirmedMousePos, setConfirmedMousePos, correctMousePosition, scanResults, resultIsAcceptable, emitValue]);

    // And of course, we need to actually display the bounding boxes.
    React.useEffect(() => {
        if (mouseState === undefined) {
            // We display both "hypothetical" and "confirmed" boxes when the
            // mouse isn't down.
            setPriorSelectedResult(undefined);
            setCurrentSelectedResult(undefined);
        } else {
            // But when the mouse is down, we only show "confirmed" boxes
            // for possible selection.
            const correctPos = correctMousePosition(mouseState);
            if (correctPos === undefined) return;

            if (!Object.is(priorSelectedResult, currentSelectedResult)) {
                setPriorSelectedResult(currentSelectedResult);
            }

            const overlapping = findOverlappingResult(scanResults.filter(resultIsAcceptable), [
                correctPos.x,
                correctPos.y,
            ]);
            if (Object.is(overlapping, currentSelectedResult)) return;

            setCurrentSelectedResult(overlapping);
        }
    }, [
        mouseState,
        setPriorSelectedResult,
        setCurrentSelectedResult,
        correctMousePosition,
        priorSelectedResult,
        currentSelectedResult,
        scanResults,
        resultIsAcceptable,
    ]);

    const cameraAvailable = typeof workingCamera === "object";
    const cameraCaptured = scanState.kind === "file" && typeof scanState.fileData === "string";
    const cameraActive = cameraAvailable && (scanState.kind === "camera" || cameraCaptured);
    const fileActive = scanState.kind === "file" && typeof scanState.fileData !== "string";

    const buttonActiveColor = theme.baseColor;
    const buttonInactiveColor = theme.primaryAccentColor;

    if (!importState.ready || orientation === undefined) {
        return (
            <CodeScannerWrapper ref={wrapperRef}>
                <ActivitySpinner />
            </CodeScannerWrapper>
        );
    }

    let cameraIcon = <StreamlineCamera fillColor={cameraActive ? buttonActiveColor : buttonInactiveColor} />;
    if (scanState.kind === "camera" && cameraAvailable) {
        cameraIcon = <Check color={buttonActiveColor} />;
    } else if (scanState.kind === "file" && typeof scanState.fileData === "string") {
        cameraIcon = <RefreshCw color={buttonActiveColor} />;
    }

    return (
        <CodeScannerWrapper ref={wrapperRef} orientation={orientation}>
            <div id={"csc-scanner-container-container"} ref={containerRef} {...mouseStateSetters}>
                <canvas className={"imgBuffer"} width={0} height={0} ref={imageCanvasRef} />
                <canvas className={"drawingBuffer"} width={0} height={0} ref={drawCanvasRef} />
                <video autoPlay={true} muted={true} playsInline={true} ref={videoRef} />
            </div>
            <div id={"csc-bottom-bar"}>
                {settingsActive ? (
                    <SettingsOverlay
                        orientation={orientation}
                        theme={theme}
                        currentPrecision={scanPrecision}
                        currentTolerance={scanTolerance}
                        onClose={toggleSettings}
                        onPrecisionChange={setScanPrecision}
                        onToleranceChange={setScanTolerance}
                        appKind={appKind}
                    />
                ) : null}
                <div id={"csc-bottom-bar-left"}>
                    <button
                        className={`csc-radial-button-${settingsActive ? "active" : "inactive"}`}
                        onClick={toggleSettings}>
                        {Settings({ size: 22, color: settingsActive ? buttonActiveColor : buttonInactiveColor })}
                    </button>
                </div>
                <div id={"csc-bottom-bar-right"}>
                    <button
                        className={`csc-radial-button-${
                            cameraAvailable ? (cameraActive ? "active" : "inactive") : "unavailable"
                        }`}
                        onClick={onCameraClick}>
                        {cameraIcon}
                    </button>
                    <label className={`csc-radial-button-${fileActive ? "active" : "inactive"}`}>
                        <input type="file" accept="image/*" ref={fileInputRef} onChange={onFileChange} />
                        <Upload color={fileActive ? buttonActiveColor : buttonInactiveColor} />
                    </label>
                </div>
            </div>
        </CodeScannerWrapper>
    );
}

export default withTheme(CodeScanner);
